import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useCaeTestingStatus } from '../../../hooks/analytics/useCaeReports.ts';
import { formatDate, RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { CAETestingStatusRow } from '../../../request/analytics/caeReports.ts';

/**
 * Report #10 — Testing Status by Control. Docx describes this as a proxy
 * pending an assignee/due-date migration; that migration (000086) already
 * landed for this build, so assignee and due date render as real columns
 * rather than the grey placeholder the docx anticipated.
 */
export const TestingStatusTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data: fetched, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeTestingStatus();
  const data = useMemo(
    () => (controlIdFilter ? (fetched ?? []).filter((r) => controlIdFilter.has(r.control_id)) : fetched),
    [fetched, controlIdFilter]
  );

  const columns = useMemo<TableColumnDef<CAETestingStatusRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {row.original.control_id} — {row.original.title}
          </span>
        ),
        widthPercent: 24,
      },
      {
        id: 'assignee_name',
        columnLabel: 'Assignee',
        header: () => 'Assignee',
        cell: ({ row }) => row.original.assignee_name ?? <span className="text-gray-400">Unassigned</span>,
        widthPercent: 14,
      },
      {
        id: 'due_date',
        columnLabel: 'Due',
        header: () => 'Due',
        cell: ({ row }) => formatDate(row.original.due_date),
        widthPercent: 10,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 12,
      },
      {
        id: 'conclusion',
        columnLabel: 'Result',
        header: () => 'Result',
        cell: ({ row }) => <StatusChip value={row.original.conclusion} fallback="Not tested" />,
        widthPercent: 12,
      },
      {
        id: 'attributes',
        columnLabel: 'Pass / Fail',
        header: () => 'Pass / Fail',
        cell: ({ row }) => (
          <span>
            <span className="text-green-600 font-medium">{row.original.attributes_passed ?? 0}</span>
            {' / '}
            <span className="text-red-600 font-medium">{row.original.attributes_failed ?? 0}</span>
          </span>
        ),
        widthPercent: 10,
      },
      {
        id: 'period',
        columnLabel: 'Period',
        header: () => 'Period',
        cell: ({ row }) =>
          `${formatDate(row.original.testing_period_start)} – ${formatDate(row.original.testing_period_end)}`,
        widthPercent: 18,
      },
    ],
    []
  );

  return (
    <AppDataTable<CAETestingStatusRow>
      data={data ?? []}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load testing status.' : null}
      emptyMessage="No controls found."
      searchable
      searchPlaceholder="Search controls or assignees..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default TestingStatusTable;
